import React, { useEffect, useMemo, useState } from "react";
import { useDeal } from "../lib/store";
import { intelApi, capitalApi, debtApi } from "../lib/api";
import { Card, useOutreach, AddToCampaignModal, ComposeEmailModal, useCompose } from "../components/shared";
import { SendToFolkModal } from "../components/SendToFolkModal";
import { SignalsPanel } from "../components/intel/SignalsPanel";
import { ThesisFinder } from "../components/intel/ThesisFinder";
import { DigestPanel } from "../components/intel/DigestPanel";
import { FamilyOffices } from "../components/intel/FamilyOffices";
import { BulkSelectBar } from "../components/BulkSelectBar";
import { dealKeyFor } from "../components/ShareRoomModal";
import * as m from "../lib/model";
import { Sparkles, ChevronDown, ExternalLink, Mail, Share2, Users2, Plus, Search, Bookmark, FolderPlus, UserSearch, Check, Globe, Linkedin, Building2 } from "lucide-react";
import { toast } from "sonner";

const SUBS = [["thesis", "Thesis"], ["checkSize", "Check size"], ["sector", "Sector"], ["geography", "Geography"], ["engagement", "Likely to engage"]];
const band = (f) => (f >= 80 ? "hot" : f >= 60 ? "warm" : f >= 40 ? "cool" : "cold");

const FitRing = ({ fit }) => (
  <div className={`fit-ring ${band(fit)}`} style={{ "--pct": `${fit}%` }} data-testid="intel-fit">
    <b>{fit}</b><span>fit</span>
  </div>
);

const Row = ({ r, audience, picked, onPick, onEmail, onCampaign, onFolk, onPipeline, onResearch, onUseContact, researching }) => {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState(null);
  const [finding, setFinding] = useState(false);
  const who = r.firm || r.name;
  const site = (r.website || "").trim();
  const siteHref = site ? (/^https?:\/\//i.test(site) ? site : `https://${site}`) : `https://www.google.com/search?q=${encodeURIComponent(who)}`;
  const liHref = `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(who)}`;
  const profHref = `https://www.google.com/search?q=${encodeURIComponent(`${who} ${audience === "lenders" ? "lender profile assets under management" : "investment firm AUM portfolio"}`)}`;
  const chips = [r.type, r.location, r.checkSize].filter(Boolean);

  const findContacts = async () => {
    setFinding(true);
    try {
      const res = await intelApi.apolloContacts({ audience, firm: who, website: site });
      setPeople(res.contacts || []);
      if (!res.contacts?.length) toast.error(`Apollo has no listed contacts for ${who}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Apollo lookup failed"); }
    setFinding(false);
  };

  return (
    <div className={`intel-row ${band(r.fit)}${picked ? " picked" : ""}`} data-testid={`intel-row-${r.id}`}>
      <label className="intel-tick">
        <input type="checkbox" checked={picked} onChange={(e) => onPick(r.id, e.target.checked)} data-testid={`intel-check-${r.id}`} />
      </label>
      <FitRing fit={r.fit} />

      <div className="intel-main">
        <div className="intel-head">
          <b className="intel-firm">{who}</b>
          {chips.map((c, i) => <em key={i}>{c}</em>)}
          <span className="intel-links">
            <a href={siteHref} target="_blank" rel="noreferrer" title={site ? `Open ${site}` : `Search the web for ${who}`} data-testid={`intel-site-${r.id}`}><Globe size={12} /></a>
            <a href={liHref} target="_blank" rel="noreferrer" title={`Find ${who} on LinkedIn`} data-testid={`intel-li-${r.id}`}><Linkedin size={12} /></a>
            <a href={profHref} target="_blank" rel="noreferrer" title={`Company details for ${who}`} data-testid={`intel-profile-${r.id}`}><Building2 size={12} /></a>
            <button disabled={finding} onClick={findContacts} title={`Find contacts at ${who} with Apollo`} data-testid={`intel-apollo-${r.id}`}>
              <UserSearch size={12} />
            </button>
            <button disabled={researching} onClick={() => { setOpen(true); onResearch(r); }} title="Research live with Perplexity" data-testid={`intel-research-${r.id}`}>
              <Search size={12} />
            </button>
          </span>
        </div>

        <div className="intel-contactline">
          {r.firm && r.name && r.name !== r.firm ? <b>{r.name}</b> : null}
          {r.title ? <em>{r.title}</em> : null}
          {r.email ? <span>{r.email}</span> : <span className="dim">no email on file — use Apollo to find one</span>}
          {r.phone ? <span>{r.phone}</span> : null}
        </div>

        <div className="intel-why">{r.why}</div>
        {r.angle ? <div className="intel-angle"><Sparkles size={11} /> {r.angle}</div> : null}

        <div className="intel-subs">
          {SUBS.map(([k, label]) => (
            <span key={k} className="intel-sub">
              <i>{label}<u>{r.scores?.[k] ?? 0}</u></i>
              <b style={{ "--w": `${r.scores?.[k] ?? 0}%` }} />
            </span>
          ))}
        </div>

        {people && !people.length ? (
          <div className="intel-research-pending" data-testid={`intel-people-none-${r.id}`}>No contacts found on Apollo for {who} — try the website or LinkedIn icon.</div>
        ) : null}
        {people?.length ? (
          <div className="intel-people" data-testid={`intel-people-${r.id}`}>
            <div className="intel-people-h">Apollo contacts · {people.length}</div>
            {people.map((p, i) => (
              <div className="intel-person" key={i} data-testid={`intel-person-${r.id}-${i}`}>
                <b>{p.name}</b>
                <em>{p.title || "—"}</em>
                <span className={p.email ? "" : "dim"}>{p.email || "no email found"}</span>
                <span className="intel-person-ln">
                  {p.phone ? <i>{p.phone}</i> : null}
                  {p.linkedin ? <a href={p.linkedin} target="_blank" rel="noreferrer" title="LinkedIn profile"><Linkedin size={11} /></a> : null}
                </span>
                <button className="btn btn-sm" onClick={() => onUseContact(r, p)} data-testid={`intel-use-person-${r.id}-${i}`}>
                  <Check size={11} style={{ marginRight: 3 }} />Use
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {open ? (
          <div className="intel-detail" data-testid={`intel-detail-${r.id}`}>
            {r.risk ? <div className="intel-risk">Watch: {r.risk}</div> : null}
            {r.research ? (
              <div className="intel-research">
                {[["activity", "Recent activity"], ["thesis", "Stated mandate"], ["checkSize", "Typical cheque"],
                  ["geography", "Where they invest"], ["signals", "Live signals"], ["verdict", "Verdict"],
                  ["confidence", "Confidence"]]
                  .filter(([k]) => r.research[k]).map(([k, label]) => (
                    <div key={k}><span>{label}</span><p>{r.research[k]}</p></div>
                  ))}
                {(r.research.sources || []).length ? (
                  <div className="intel-sources">
                    {r.research.sources.map((s, i) => (
                      <a key={i} href={s} target="_blank" rel="noreferrer">{`source ${i + 1}`}<ExternalLink size={9} /></a>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="intel-research-pending">{researching ? "Researching live…" : "No live research yet — hit the magnifier to pull current web intelligence."}</div>
            )}
          </div>
        ) : null}
      </div>

      <div className="intel-acts">
        <button className="btn btn-sm" disabled={!r.email} onClick={() => onEmail(r)} title="Compose email" data-testid={`intel-email-${r.id}`}><Mail size={12} /></button>
        <button className="btn btn-sm" disabled={!r.email} onClick={() => onCampaign(r)} title="Add to campaign" data-testid={`intel-campaign-${r.id}`}><Share2 size={12} /></button>
        <button className="btn btn-sm" disabled={!r.email} onClick={() => onFolk(r)} title="Send to Folk" data-testid={`intel-folk-${r.id}`}><Users2 size={12} /></button>
        <button className="btn btn-sm" onClick={() => onPipeline(r)} title={audience === "lenders" ? "Add to debt pipeline" : "Add to capital pipeline"} data-testid={`intel-pipeline-${r.id}`}><Plus size={12} /></button>
        <button className="btn btn-sm" onClick={() => setOpen((o) => !o)} title={open ? "Collapse" : "Expand"} data-testid={`intel-expand-${r.id}`}>
          <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s ease" }} />
        </button>
      </div>
    </div>
  );
};

export default function Intelligence() {
  const { M, dealId, dealName } = useDeal();
  const dealKey = dealKeyFor(dealId, M);
  const outreach = useOutreach();
  const compose = useCompose();
  const [audience, setAudience] = useState("investors");
  const [sigView, setSigView] = useState("signals");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [folk, setFolk] = useState(null);
  const [picks, setPicks] = useState({});
  const [webPicks, setWebPicks] = useState({});
  const [webFolder, setWebFolder] = useState("");
  const [folder, setFolder] = useState("");
  const [saved, setSaved] = useState([]);
  const [rsch, setRsch] = useState({});      // live research kept locally so polling never wipes it
  const [rBusy, setRBusy] = useState({});
  const [cmap, setCmap] = useState({});      // contacts picked from Apollo, merged over the run
  const equityAsk = useMemo(() => Math.round(m.exitReturns(M).equityInvested || 0), [M]);
  const [ctx, setCtx] = useState(null);

  const baseCtx = useMemo(() => ({
    dealKey, dealName: M.dealName || dealName || "",
    industry: M.industry || "", stage: "Raising", raiseSize: equityAsk,
    minCheck: Math.max(25000, Math.round(equityAsk / 20)), maxCheck: Math.round(equityAsk / 2),
    geography: M.address || "", useOfFunds: M.dealType ? `${M.dealType} acquisition` : "Acquisition",
    dealType: M.dealType || "", revenue: M.ttmRevenue || 0, ebitda: M.sde || 0, notes: "",
    research: 25, discover: true, discoverTarget: 50,
  }), [dealKey, dealName, M, equityAsk]);

  useEffect(() => { setCtx((c) => c || baseCtx); }, [baseCtx]);
  useEffect(() => {
    setPicks({});
    setWebPicks({});
    setRsch({});
    setCmap({});
    if (audience === "signals" || audience === "family") return;
    intelApi.last({ dealKey, audience }).then((r) => {
      setRun(r && r.id ? r : null);
      if (r?.status === "running") setBusy(true);   // a run started earlier is still going server-side
    }).catch(() => setRun(null));
    intelApi.searches({ dealKey }).then((r) => setSaved(r.searches || [])).catch(() => setSaved([]));
  }, [dealKey, audience]);

  const field = (k, v) => setCtx((c) => ({ ...c, [k]: v }));

  // The run keeps going server-side; we poll and fill the shortlist in as it scores, researches and discovers.
  const match = async () => {
    setBusy(true);
    try {
      const started = await intelApi.match({ ...ctx, audience });
      setRun(started);
      toast.success(`Scanning ${started.scanned.toLocaleString()} ${audience}…`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Matching failed"); setBusy(false); }
  };

  useEffect(() => {
    if (!busy || !run?.id) return;
    const t = setInterval(async () => {
      try {
        const r = await intelApi.run(run.id);
        setRun(r);
        if (r.status !== "running") {
          setBusy(false);
          if (r.status === "error") toast.error("The run stopped early — partial results are shown");
          else toast.success(`${r.matches.length} ranked · ${r.researched} researched live${r.discovered?.length ? ` · ${r.discovered.length} new firms found` : ""}`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [busy, run?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const research = async (r) => {
    const tid = toast.loading(`Researching ${r.firm || r.name}…`);
    setRBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const data = await intelApi.research({ dealKey, audience, name: r.name, firm: r.entity || r.firm || "", website: r.website || "", context: { ...ctx, audience } });
      setRsch((prev) => ({ ...prev, [r.id]: data }));
      toast.success("Research added", { id: tid });
    } catch (e) { toast.error(e?.response?.data?.detail || "Research failed", { id: tid }); }
    setRBusy((b) => ({ ...b, [r.id]: false }));
  };

  const useContact = (r, p) => {
    setCmap((prev) => ({ ...prev, [r.id]: { name: p.name || r.name, contact: p.name, email: p.email || r.email,
      phone: p.phone || r.phone, linkedin: p.linkedin || r.linkedin, title: p.title || r.title,
      firm: r.firm || r.name, website: r.website || p.website } }));
    toast.success(`${p.name} set as the contact${p.email ? ` · ${p.email}` : " — no email found"}`);
  };

  const emailOne = (r) => compose.start({
    title: audience === "lenders" ? "Email Lender" : "Email Investor",
    recipients: [{ name: r.name, email: r.email, firm: r.firm }], dealKey,
    subject: `${M.dealName || "Acquisition opportunity"} — ${audience === "lenders" ? "financing request" : "investment opportunity"}`,
    body: `Hi ${(r.name || "there").split(" ")[0]},\n\n${r.angle || "I thought this might fit your mandate."}\n\n`
      + `${M.dealName || "The business"}: ${M.industry || ""} · TTM revenue ${m.fmt$(M.ttmRevenue)} · adjusted SDE ${m.fmt$(M.sde)}.\n`
      + `We are raising ${m.fmt$(equityAsk)} of ${audience === "lenders" ? "senior debt" : "LP equity"}.\n\nWorth a look?\n`,
  });

  const toPipeline = async (r) => {
    try {
      if (audience === "lenders") {
        await debtApi.create({ lender: r.firm || r.name, contact: r.name || "", email: r.email || "", dealKey, dealName: M.dealName || "", stage: "Contacted", type: r.type || "Bank" });
      } else {
        await capitalApi.create({ name: r.name || r.firm, firm: r.firm || "", email: r.email || "", dealKey, dealName: M.dealName || "", stage: "Prospect", type: r.type || "LP" });
      }
      toast.success(`${r.firm || r.name} added to the ${audience === "lenders" ? "debt" : "capital"} pipeline`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to the pipeline"); }
  };

  const saveSearch = async () => {
    const name = window.prompt("Name this search", `${M.dealName || "Deal"} — ${audience} ${ctx.stage || ""}`.trim());
    if (name === null) return;
    try {
      await intelApi.saveSearch({ name, context: { ...ctx, audience } });
      setSaved(((await intelApi.searches({ dealKey })).searches) || []);
      toast.success(`Saved “${name}” — reload it any time from the dropdown`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the search"); }
  };

  const loadSearch = (id) => {
    const hit = saved.find((s) => s.id === id);
    if (!hit) return;
    setCtx({ ...hit.context, dealKey, dealName: M.dealName || hit.context.dealName || "" });
    setAudience(hit.context.audience || "investors");
    toast.success(`Loaded “${hit.name}”`);
  };

  const matches = useMemo(() => (run?.matches || []).map((r) => ({ ...r, ...(cmap[r.id] || {}), research: rsch[r.id] || r.research })), [run, cmap, rsch]);
  const decorate = useMemo(() => (r) => ({ ...r, ...(cmap[r.id] || {}), research: rsch[r.id] || r.research }), [cmap, rsch]);
  const pickedRows = useMemo(() => matches.filter((r) => picks[r.id]), [matches, picks]);
  const webRows = useMemo(() => (run?.discovered || []).map(decorate), [run, decorate]);
  const pickedWeb = useMemo(() => webRows.filter((r) => webPicks[r.id]), [webRows, webPicks]);

  const saveWebToPipeline = async () => {
    const name = (webFolder || "").trim() || `Web leads — ${new Date().toISOString().slice(0, 10)}`;
    try {
      const res = await intelApi.toPipeline({
        audience, folder: name, dealKey, dealName: M.dealName || "",
        rows: pickedWeb.map((r) => ({ ...r, name: r.contact || r.name || r.firm, firm: r.firm || r.name || "" })),
      });
      setWebPicks({});
      toast.success(`${res.added.length} saved to ${audience === "lenders" ? "Debt Lenders" : "Capital Markets"} · folder “${name}”${res.skipped ? ` · ${res.skipped} already there` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save them"); }
  };

  const promoteSelected = async () => {
    try {
      const res = await intelApi.promote({ audience, rows: pickedWeb });
      setWebPicks({});
      toast.success(`${res.added.length} added to ${audience === "lenders" ? "Debt Lenders" : "Investors"}${res.skipped ? ` · ${res.skipped} already there` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them"); }
  };

  const saveToPipeline = async () => {
    const name = (folder || "").trim() || `Intelligence — ${new Date().toISOString().slice(0, 10)}`;
    try {
      const res = await intelApi.toPipeline({
        audience, folder: name, dealKey, dealName: M.dealName || "",
        rows: pickedRows,
      });
      setPicks({});
      toast.success(`${res.added.length} saved to ${audience === "lenders" ? "Debt Lenders" : "Capital Markets"} · folder “${name}”${res.skipped ? ` · ${res.skipped} already there` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save them"); }
  };

  const promote = async () => {
    try {
      const res = await intelApi.promote({ audience, rows: run.discovered });
      toast.success(`${res.added.length} added to ${audience === "lenders" ? "Debt Lenders" : "Investors"}${res.skipped ? ` · ${res.skipped} already there` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them"); }
  };

  if (!ctx) return null;

  const tabs = (
    <div className="tone-bar" data-testid="intel-audience">
      <button className={audience === "investors" ? "active" : ""} onClick={() => setAudience("investors")} data-testid="intel-aud-investors">Investors</button>
      <button className={audience === "lenders" ? "active" : ""} onClick={() => setAudience("lenders")} data-testid="intel-aud-lenders">Lenders</button>
      <button className={audience === "signals" ? "active" : ""} onClick={() => setAudience("signals")} data-testid="intel-aud-signals">Signal</button>
      <button className={audience === "family" ? "active" : ""} onClick={() => setAudience("family")} data-testid="intel-aud-family">Family Offices</button>
    </div>
  );

  if (audience === "family") return (
    <div className="page" data-testid="page-intel">
      <div className="page-head">
        <div className="page-title">Intelligence · Family Offices</div>
        <div className="page-sub">Named family offices and family-office-backed permanent capital — evidence of patient capital, evidence they back independent sponsors, cheque size and how to approach them</div>
      </div>
      <div className="intel-tabrow"><span /> {tabs}</div>
      <FamilyOffices dealKey={dealKey} dealName={M.dealName || dealName || ""} industry={M.industry || ""}
        geography={M.address || ""} raiseSize={equityAsk} platform={M.dealName || ""} />
    </div>
  );

  if (audience === "signals") return (
    <div className="page" data-testid="page-intel">
      <div className="page-head">
        <div className="page-title">Intelligence · Signal</div>
        <div className="page-sub">A web scout on the industry — who is consolidating, which theses are forming, where the white space is, who is lending and what things are trading for</div>
      </div>
      <div className="intel-tabrow">
        <div className="tone-bar" data-testid="signal-views">
          {[["signals", "Market signals"], ["thesis", "Thesis finder"], ["digest", "Weekly digest"]].map(([id, label]) => (
            <button key={id} className={sigView === id ? "active" : ""} onClick={() => setSigView(id)} data-testid={`signal-view-${id}`}>{label}</button>
          ))}
        </div>
        {tabs}
      </div>
      {sigView === "thesis"
        ? <ThesisFinder dealKey={dealKey} dealName={M.dealName || dealName || ""} industry={M.industry || ""} geography={M.address || ""} />
        : sigView === "digest"
          ? <DigestPanel />
          : <SignalsPanel dealKey={dealKey} dealName={M.dealName || dealName || ""} industry={M.industry || ""} geography={M.address || ""} />}
    </div>
  );

  return (
    <div className="page" data-testid="page-intel">
      <div className="page-head">
        <div className="page-title">Intelligence</div>
        <div className="page-sub">Semantic matching across your database plus live web research — who is most likely to fund this raise, and why</div>
      </div>

      <Card title="Matching Input" testid="intel-input" right={tabs}>
        <div className="intel-grid">
          <div className="field"><label>Industry</label><input className="inp inp-text" value={ctx.industry} onChange={(e) => field("industry", e.target.value)} data-testid="intel-industry" /></div>
          <div className="field"><label>Stage of raise</label>
            <select className="inp" value={ctx.stage} onChange={(e) => field("stage", e.target.value)} data-testid="intel-stage">
              {["Exploring", "Raising", "Soft-circled", "Closing"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div className="field"><label>Raise size ($)</label><input className="inp inp-text" type="number" value={ctx.raiseSize} onChange={(e) => field("raiseSize", +e.target.value)} data-testid="intel-raise" /></div>
          <div className="field"><label>Check size min ($)</label><input className="inp inp-text" type="number" value={ctx.minCheck} onChange={(e) => field("minCheck", +e.target.value)} data-testid="intel-mincheck" /></div>
          <div className="field"><label>Check size max ($)</label><input className="inp inp-text" type="number" value={ctx.maxCheck} onChange={(e) => field("maxCheck", +e.target.value)} data-testid="intel-maxcheck" /></div>
          <div className="field"><label>Geography</label><input className="inp inp-text" value={ctx.geography} onChange={(e) => field("geography", e.target.value)} data-testid="intel-geo" /></div>
          <div className="field" style={{ gridColumn: "span 2" }}><label>Use of funds</label><input className="inp inp-text" value={ctx.useOfFunds} onChange={(e) => field("useOfFunds", e.target.value)} data-testid="intel-use" /></div>
          <div className="field"><label>Research depth (top N)</label><input className="inp inp-text" type="number" min="0" max="25" value={ctx.research} onChange={(e) => field("research", Math.max(0, Math.min(25, +e.target.value)))} data-testid="intel-depth" /></div>
          <div className="field"><label>Web leads to find (50–150)</label><input className="inp inp-text" type="number" min="50" max="150" step="10" value={ctx.discoverTarget} onChange={(e) => field("discoverTarget", Math.max(50, Math.min(150, +e.target.value || 50)))} data-testid="intel-discover-target" /></div>
        </div>
        <div className="field" style={{ marginTop: 10 }}><label>Anything else the matcher should weigh</label>
          <textarea className="inp inp-text" rows={2} value={ctx.notes} onChange={(e) => field("notes", e.target.value)} placeholder="e.g. seller wants a fast close; prefer LPs who have backed independent sponsors before" data-testid="intel-notes" />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={match} disabled={busy} data-testid="intel-run">
            <Sparkles size={13} style={{ marginRight: 6 }} />{busy ? "Matching…" : `Run match on ${audience}`}
          </button>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5 }}>
            <input type="checkbox" checked={ctx.discover} onChange={(e) => field("discover", e.target.checked)} data-testid="intel-discover" />
            Also discover firms we don't have yet (web first)
          </label>
          <button className="btn" onClick={saveSearch} data-testid="intel-save-search"><Bookmark size={13} style={{ marginRight: 5 }} />Save search</button>
          {saved.length ? (
            <select className="inp" style={{ width: 200 }} value="" onChange={(e) => e.target.value && loadSearch(e.target.value)} data-testid="intel-saved-list">
              <option value="">Saved searches ({saved.length})</option>
              {saved.map((sv) => <option key={sv.id} value={sv.id}>{sv.name}</option>)}
            </select>
          ) : null}
          {busy && run ? (
            <span className="ir-fine" data-testid="intel-progress">
              {run.phase === "discovering" ? `Searching the web — ${run.discovered?.length || 0}/${run.discoverTarget || 0} net-new firms found…`
                : run.phase === "scoring-web" ? `Scoring ${run.discovered?.length || 0} web leads with AI…`
                  : run.phase === "scoring" ? `Scoring ${run.scored}/${run.shortlisted} from your database…`
                    : run.phase === "researching" ? `Researching ${run.researched}/${run.researchTarget} live…` : "Shortlisting…"}
            </span>
          ) : null}
          {run && !busy ? <span className="ir-fine" data-testid="intel-meta">Last run {String(run.ranAt || "").slice(0, 16).replace("T", " ")} · {run.discovered?.length || 0} web leads · {run.scanned?.toLocaleString()} scanned · {run.shortlisted} shortlisted · {run.researched} researched</span> : null}
        </div>
      </Card>

      {webRows.length || busy ? (
        <Card title={`Discovered on the web — not in your database${webRows.length ? ` · ${webRows.length}` : ""}`} style={{ marginTop: 12 }} testid="intel-discovered" right={(
          <button className="btn btn-primary" onClick={promote} disabled={!webRows.length} data-testid="intel-promote">Add all to {audience === "lenders" ? "Debt Lenders" : "Investors"}</button>
        )}>
          {!webRows.length
            ? <div className="docs-empty" data-testid="intel-discovering">Perplexity is searching the web for firms you don't have yet…</div>
            : (<>
              <BulkSelectBar count={pickedWeb.length} total={webRows.length} withEmail={pickedWeb.filter((r) => r.email).length}
                noun="web leads" allSelected={webRows.length > 0 && pickedWeb.length === webRows.length}
                onSelectAll={() => setWebPicks(Object.fromEntries(webRows.map((r) => [r.id, true])))}
                onClear={() => setWebPicks({})} testid="intel-web-bulk">
                <input className="inp inp-text" style={{ width: 210 }} value={webFolder} onChange={(e) => setWebFolder(e.target.value)}
                  placeholder={`Web leads — ${new Date().toISOString().slice(0, 10)}`} data-testid="intel-web-folder" />
                <button className={`btn btn-sm ${pickedWeb.length ? "btn-primary" : ""}`} disabled={!pickedWeb.length} onClick={saveWebToPipeline} data-testid="intel-web-save-pipeline">
                  <FolderPlus size={12} style={{ marginRight: 4 }} />Save {pickedWeb.length || ""} to {audience === "lenders" ? "Debt Lenders" : "Capital Markets"}
                </button>
                <button className="btn btn-sm" disabled={!pickedWeb.length} onClick={promoteSelected} data-testid="intel-web-add-crm">
                  <Plus size={12} style={{ marginRight: 4 }} />Add {pickedWeb.length || ""} to {audience === "lenders" ? "Debt Lenders" : "Investors"} CRM
                </button>
                <button className="btn btn-sm" disabled={!pickedWeb.filter((r) => r.email).length} data-testid="intel-web-campaign"
                  onClick={() => outreach.start(pickedWeb.filter((r) => r.email).map((r) => ({ name: r.contact || r.name, email: r.email, firm: r.firm || r.name })), `${M.dealName || "Deal"} — Web leads`)}>
                  <Share2 size={12} style={{ marginRight: 4 }} />Add to campaign
                </button>
              </BulkSelectBar>
              {webRows.map((d) => (
                <Row key={d.id} r={{ ...d, firm: d.firm || d.name, name: d.contact || d.name || d.firm, type: d.type || (d.location || ""), why: d.why || d.thesis }}
                  audience={audience} picked={!!webPicks[d.id]} onPick={(id, on) => setWebPicks((p) => ({ ...p, [id]: on }))}
                  onResearch={research} onEmail={emailOne} onPipeline={toPipeline} onUseContact={useContact} researching={!!rBusy[d.id]}
                  onCampaign={(x) => outreach.start([{ name: x.name, email: x.email, firm: x.firm }], `${M.dealName || "Deal"} — Web leads`)}
                  onFolk={(x) => setFolk(x)} />
              ))}
            </>)}
        </Card>
      ) : null}

      <Card title={`From your database — ranked shortlist${matches.length ? ` · ${matches.length}` : ""}`} style={{ marginTop: 12 }} testid="intel-results">
        {!matches.length
          ? <div className="docs-empty" data-testid="intel-empty">{busy
              ? "Your own list is scored right after the web sweep finishes — the ranked shortlist lands here."
              : "Nothing matched yet — set the context above and hit “Run match”."}</div>
          : (<>
          <BulkSelectBar count={pickedRows.length} total={matches.length} withEmail={pickedRows.filter((r) => r.email).length}
            noun="matches" allSelected={matches.length > 0 && pickedRows.length === matches.length}
            onSelectAll={() => setPicks(Object.fromEntries(matches.map((r) => [r.id, true])))}
            onClear={() => setPicks({})} testid="intel-bulk">
            <input className="inp inp-text" style={{ width: 210 }} value={folder} onChange={(e) => setFolder(e.target.value)}
              placeholder={`Intelligence — ${new Date().toISOString().slice(0, 10)}`} data-testid="intel-folder" />
            <button className={`btn btn-sm ${pickedRows.length ? "btn-primary" : ""}`} disabled={!pickedRows.length} onClick={saveToPipeline} data-testid="intel-save-pipeline">
              <FolderPlus size={12} style={{ marginRight: 4 }} />Save {pickedRows.length || ""} to {audience === "lenders" ? "Debt Lenders" : "Capital Markets"}
            </button>
            <button className="btn btn-sm" disabled={!pickedRows.filter((r) => r.email).length} data-testid="intel-bulk-campaign"
              onClick={() => outreach.start(pickedRows.filter((r) => r.email).map((r) => ({ name: r.name, email: r.email, firm: r.firm })), `${M.dealName || "Deal"} — Intelligence`)}>
              <Share2 size={12} style={{ marginRight: 4 }} />Add to campaign
            </button>
          </BulkSelectBar>
          {matches.map((r) => (
            <Row key={r.id} r={r} audience={audience} picked={!!picks[r.id]} onPick={(id, on) => setPicks((p) => ({ ...p, [id]: on }))}
              onResearch={research} onEmail={emailOne} onPipeline={toPipeline} onUseContact={useContact} researching={!!rBusy[r.id]}
              onCampaign={(x) => outreach.start([{ name: x.name, email: x.email, firm: x.firm }], `${M.dealName || "Deal"} — Intelligence`)}
              onFolk={(x) => setFolk(x)} />
          ))}
          </>)}
      </Card>

      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts} defaultGroupName={outreach.defaultName} source={audience} />
      <SendToFolkModal open={!!folk} onClose={() => setFolk(null)} source={audience === "lenders" ? "lenders" : "investors"}
        label={audience === "lenders" ? "Debt Lenders" : "Investors"} dealName={M.dealName || ""}
        contacts={folk ? [{ id: folk.id, name: folk.name, email: folk.email, company: folk.firm || "", focus: folk.why || "" }] : []} />
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </div>
  );
}
